import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Alert, Attributes, Card, DataStatus} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {TelephonyRecordingSettings, useGetTelephonyRecordingSettingsQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {getUserName, usePermissions} from '@dms/shared';

import {
  buildArray,
  DOT_CHARACTER,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useBoolean,
  useOnMount,
} from 'shared/web';

import {RECORDING_SETTINGS_SECTION_ID} from '../../../constants/RECORDING_SETTINGS_SECTION_ID';
import {TelephonyRecordingSettingsForm} from './TelephonyRecordingSettingsForm';

const getInboundLabel = (notice: TelephonyRecordingSettings['noticeInbound']) =>
  notice === 'operator'
    ? i18n.t('page.settings.telephony.recordingNoticeInboundOperator')
    : i18n.t('page.settings.telephony.recordingNoticeBeforeRinging');

const getOutboundLabel = (notice: TelephonyRecordingSettings['noticeOutbound']) =>
  notice === 'operator'
    ? i18n.t('page.settings.telephony.recordingNoticeOutboundOperator')
    : i18n.t('page.settings.telephony.recordingNoticeOnAnswer');

const getLanguageName = (language: string | null) =>
  isNil(language)
    ? null
    : (new Intl.DisplayNames([i18n.language ?? 'en'], {type: 'language'}).of(language) ?? language);

/** Arriving from a line's "Settings › Telephony › Recording" link scrolls the card into view. */
function ScrollIntoViewOnArrival() {
  useOnMount(() => {
    if (window.location.hash === `#${RECORDING_SETTINGS_SECTION_ID}`) {
      document.getElementById(RECORDING_SETTINGS_SECTION_ID)?.scrollIntoView({block: 'start'});
    }
  });

  return null;
}

/**
 * Tenant-wide recording settings: how long recordings are kept and how callers are told about
 * them. Applies to every line whose recording is switched on; consent stays per line.
 */
export function TelephonyRecordingSettingsCard(props: RequiredTestIdProps) {
  const formatDateTime = useDateTimeFormatter();
  const [isEditing, startEditing, stopEditing] = useBoolean(false);
  const [canWrite] = usePermissions({permissionKeys: ['telephonySettingsWrite']});
  const {data: settings, isLoading, isError, refetch} = useGetTelephonyRecordingSettingsQuery();

  // The PUT returns nothing: read the new who / when before leaving the form.
  const handleSaved = () => refetch().unwrap().then(stopEditing);

  const {data: confirmedBy} = useGetUserQuery(
    isNotNil(settings?.operatorNoticeConfirmedBy)
      ? {id: settings.operatorNoticeConfirmedBy}
      : skipToken
  );
  const {data: changedBy} = useGetUserQuery(
    isNotNil(settings?.changedBy) ? {id: settings.changedBy} : skipToken
  );

  const formatDate = (value: string | null) =>
    isNil(value) ? null : formatDateTime('dateTimeShort', parseDate(value));

  const confirmedAt = formatDate(settings?.operatorNoticeConfirmedAt ?? null);
  const confirmedLabel = isNil(confirmedAt)
    ? null
    : i18n.t('page.settings.telephony.recordingOperatorConfirmed', {
        name: getUserName(confirmedBy) ?? '',
        date: confirmedAt,
      });
  const changedAt = formatDate(settings?.changedAt ?? null);
  const changedLabel = isNil(changedAt)
    ? null
    : [
        i18n.t('page.settings.telephony.recordingChangedAt', {date: changedAt}),
        getUserName(changedBy),
      ]
        .filter(isNotNil)
        .join(` ${DOT_CHARACTER} `);

  const notes = (values: Pick<TelephonyRecordingSettings, 'noticeInbound' | 'noticeOutbound'>) => (
    <>
      <Show when={values.noticeInbound === 'operator' || values.noticeOutbound === 'operator'}>
        <Alert
          type="inline"
          variant="warning"
          message={buildArray<string>()
            .add(i18n.t('page.settings.telephony.recordingOperatorWarning'))
            .whenNotNil(confirmedLabel)}
          data-testid={suffixTestId('operatorWarning', props)}
        />
      </Show>
      <HStack spacing={2} align="center">
        <Icon value="action/info" size={4} color="text.secondary" />
        <Text size="small" color="secondary">
          {i18n.t('page.settings.telephony.recordingAssistantInfo')}
        </Text>
      </HStack>
    </>
  );

  const actions = buildArray<Action>().when(canWrite && !isEditing && isNotNil(settings), {
    type: 'iconButtonWithTooltip',
    icon: 'image/edit',
    tooltip: {label: i18n.t('general.actions.edit')},
    onClick: startEditing,
    'data-testid': suffixTestId('edit', props),
  });

  return (
    <Box id={RECORDING_SETTINGS_SECTION_ID}>
      <Card
        title={i18n.t('page.settings.telephony.recordingSettingsCardTitle')}
        actions={actions}
        data-testid={suffixTestId('card', props)}
      >
        <DataStatus isLoading={isLoading} isError={isError} minHeight={30}>
          <Show when={settings}>
            {(loadedSettings) => (
              <VStack spacing={4}>
                <ScrollIntoViewOnArrival />
                <Text color="secondary" size="small">
                  {i18n.t('page.settings.telephony.recordingSettingsDescription')}
                </Text>
                {isEditing ? (
                  <TelephonyRecordingSettingsForm
                    settings={loadedSettings}
                    languageName={getLanguageName(loadedSettings.noticeLanguage)}
                    notes={notes}
                    onSaved={handleSaved}
                    onDiscard={stopEditing}
                    data-testid={suffixTestId('form', props)}
                  />
                ) : (
                  <>
                    <Attributes
                      size="quarter"
                      rows={[
                        {
                          label: i18n.t('page.settings.telephony.recordingRetention'),
                          value: i18n.t('page.settings.telephony.recordingRetentionDays', {
                            count: loadedSettings.retentionDays,
                          }),
                        },
                        {
                          label: i18n.t('page.settings.telephony.recordingNoticeInbound'),
                          value: getInboundLabel(loadedSettings.noticeInbound),
                        },
                        {
                          label: i18n.t('page.settings.telephony.recordingNoticeOutbound'),
                          value: getOutboundLabel(loadedSettings.noticeOutbound),
                        },
                        {
                          label: i18n.t('page.settings.telephony.recordingNoticeText'),
                          value:
                            loadedSettings.noticeText ??
                            i18n.t('page.settings.telephony.recordingNoticeTextDefault', {
                              text: loadedSettings.defaultNoticeText,
                            }),
                        },
                      ]}
                      data-testid={suffixTestId('attributes', props)}
                    />
                    {notes(loadedSettings)}
                    <Show when={changedLabel}>
                      {(label) => (
                        <Text
                          color="secondary"
                          size="xSmall"
                          data-testid={suffixTestId('changed', props)}
                        >
                          {label}
                        </Text>
                      )}
                    </Show>
                  </>
                )}
              </VStack>
            )}
          </Show>
        </DataStatus>
      </Card>
    </Box>
  );
}
